import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  LockReset,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  Lock,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ipayNet, newServer } from 'constants/constants';
import { HomeConfig } from 'Config/home.config';

interface PasswordRequirement {
  label: string;
  regex: RegExp;
  met: boolean;
}

export const ForgotPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('error');

  const query = new URLSearchParams(location.search);
  const token = query.get('token');
  const email = query.get('email');

  // Validación de requisitos de contraseña
  const getPasswordRequirements = (pwd: string): PasswordRequirement[] => [
    { label: 'At least 10 characters', regex: /.{10,}/, met: /.{10,}/.test(pwd) },
    { label: 'One lowercase letter', regex: /[a-z]/, met: /[a-z]/.test(pwd) },
    { label: 'One uppercase letter', regex: /[A-Z]/, met: /[A-Z]/.test(pwd) },
    { label: 'One number', regex: /[0-9]/, met: /[0-9]/.test(pwd) },
    { label: 'One special character (@!#$%^&+=)', regex: /[@!#$%^&+=]/, met: /[@!#$%^&+=]/.test(pwd) },
  ];

  const passwordRequirements = getPasswordRequirements(password);
  const allRequirementsMet = passwordRequirements.every(req => req.met);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const isFormValid = allRequirementsMet && passwordsMatch;

  const showNotification = (message: string, severity: 'success' | 'error' = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleSubmit = async () => {
    if (!isFormValid || loading) return;

    if (!token || !email) {
      showNotification('Invalid reset link. Please request a new password reset.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${newServer}password/reset/${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ipayNet.apiToken,
        },
        body: JSON.stringify({
          email,
          password,
          confirm_password: confirmPassword,
          token,
        }),
      });

      setLoading(false);

      if (res.status === 200) {
        showNotification('Password updated successfully! Redirecting to login...', 'success');
        setTimeout(() => {
          navigate('/backoffice');
        }, 2000);
      } else {
        const errorText = await res.text();
        try {
          const errorJson = JSON.parse(errorText);
          showNotification(errorJson.message || 'Error updating password. Please try again.');
        } catch {
          showNotification(errorText || 'Error updating password. Please try again.');
        }
      }
    } catch (error) {
      setLoading(false);
      console.error('handleSubmit error:', error);
      showNotification('Connection error. Please try again.');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isFormValid && !loading) {
      handleSubmit();
    }
  };

  // Verificar si tenemos token y email válidos
  if (!token || !email) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f1f5f9',
          p: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            bgcolor: 'white',
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e2e8f0',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Cancel sx={{ color: '#ef4444', fontSize: 28 }} />
          </Box>
          <Typography variant="h5" sx={{ color: '#1e293b', fontWeight: 700, mb: 1 }}>
            Invalid Reset Link
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
            This password reset link is invalid or has expired. Please request a new one.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/backoffice')}
            sx={{
              py: 1.5,
              borderRadius: 2,
              bgcolor: '#E91E8C',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { bgcolor: '#c41874' },
            }}
          >
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f1f5f9',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          bgcolor: 'white',
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: '#E91E8C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <LockReset sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              color: '#1e293b',
              fontWeight: 700,
              mb: 0.5,
            }}
          >
            Reset Password
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#64748b' }}
          >
            Create a new password for your {HomeConfig.merchatName} account
          </Typography>
        </Box>

        {/* Form */}
        <Box component="form" onKeyDown={handleKeyDown}>
          {/* New Password */}
          <TextField
            label="New Password"
            variant="outlined"
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
            autoFocus
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#E91E8C' },
              },
              '& .MuiInputLabel-root': {
                color: '#64748b',
                '&.Mui-focused': { color: '#E91E8C' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: '#94a3b8' }}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Password Requirements */}
          {password.length > 0 && (
            <Box
              sx={{
                bgcolor: '#f8fafc',
                borderRadius: 2,
                p: 2,
                mb: 2,
                border: '1px solid #e2e8f0',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  fontWeight: 600,
                  display: 'block',
                  mb: 1,
                }}
              >
                Password Requirements:
              </Typography>
              <List dense sx={{ py: 0 }}>
                {passwordRequirements.map((req, index) => (
                  <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {req.met ? (
                        <CheckCircle sx={{ fontSize: 16, color: '#22c55e' }} />
                      ) : (
                        <Cancel sx={{ fontSize: 16, color: '#94a3b8' }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={req.label}
                      primaryTypographyProps={{
                        variant: 'caption',
                        sx: {
                          color: req.met ? '#22c55e' : '#64748b',
                          fontWeight: req.met ? 500 : 400,
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Confirm Password */}
          <TextField
            label="Confirm Password"
            variant="outlined"
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
            error={confirmPassword.length > 0 && !passwordsMatch}
            helperText={
              confirmPassword.length > 0 && !passwordsMatch
                ? 'Passwords do not match'
                : ''
            }
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#E91E8C' },
              },
              '& .MuiInputLabel-root': {
                color: '#64748b',
                '&.Mui-focused': { color: '#E91E8C' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: '#94a3b8' }}
                  >
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Submit Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
            sx={{
              py: 1.5,
              borderRadius: 2,
              bgcolor: '#E91E8C',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 2px 8px rgba(233, 30, 140, 0.25)',
              '&:hover': {
                bgcolor: '#c41874',
                boxShadow: '0 4px 12px rgba(233, 30, 140, 0.35)',
              },
              '&:disabled': {
                bgcolor: '#e2e8f0',
                color: '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Update Password'
            )}
          </Button>

          {/* Back to Login */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography
              variant="body2"
              onClick={() => navigate('/backoffice')}
              sx={{
                color: '#E91E8C',
                cursor: 'pointer',
                fontSize: '0.875rem',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Back to Login
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 3,
            color: '#94a3b8',
            fontSize: '0.7rem',
            lineHeight: 1.6,
          }}
        >
          By resetting your password, you agree to our{' '}
          <Box
            component="span"
            onClick={() => navigate('/terms')}
            sx={{
              color: '#E91E8C',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Terms
          </Box>{' '}
          and{' '}
          <Box
            component="span"
            onClick={() => navigate('/policies')}
            sx={{
              color: '#E91E8C',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Privacy Policy
          </Box>
        </Typography>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
